import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteValidation } from '@/hooks/useInventoryValidations';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { InventoryValidation } from '@/types/entities';
import { format, parseISO } from 'date-fns';

interface DeleteValidationDialogProps {
  restaurantId: string;
  validation: InventoryValidation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteValidationDialog({
  restaurantId,
  validation,
  open,
  onOpenChange,
}: DeleteValidationDialogProps) {
  const { t, i18n } = useTranslation();
  const deleteMutation = useDeleteValidation(restaurantId);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    deleteMutation.mutate(
      { validationId: validation.id },
      {
        onSettled: () => {
          setIsDeleting(false);
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('inventory.deleteValidation.title')}</DialogTitle>
          <DialogDescription>{t('inventory.deleteValidation.confirmation')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Validation details */}
          <div className="p-4 bg-muted rounded-md space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {t('inventory.validationHistory.ingredient')}:
              </span>
              <span className="text-sm">
                {i18n.language === 'ar'
                  ? validation.ingredient?.nameAr
                  : validation.ingredient?.nameFr}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {t('inventory.validationHistory.date')}:
              </span>
              <span className="text-sm">
                {format(parseISO(validation.validationDate), 'dd/MM/yyyy')}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {t('inventory.validationHistory.calculated')}:
              </span>
              <span className="text-sm">
                {validation.calculatedQuantity.toFixed(3)} {validation.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {t('inventory.validationHistory.actual')}:
              </span>
              <span className="text-sm">
                {validation.actualQuantity.toFixed(3)} {validation.unit}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {t('inventory.validationHistory.difference')}:
              </span>
              <Badge
                variant={
                  validation.difference === 0
                    ? 'secondary'
                    : validation.difference < 0
                      ? 'destructive'
                      : 'default'
                }
              >
                {validation.difference > 0 ? '+' : ''}
                {validation.difference.toFixed(3)} {validation.unit}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {t('inventory.validationHistory.costImpact')}:
              </span>
              <span
                className={`text-sm font-semibold ${
                  validation.costVariance < 0
                    ? 'text-red-600'
                    : validation.costVariance > 0
                      ? 'text-green-600'
                      : 'text-blue-600'
                }`}
              >
                {validation.costVariance > 0 ? '+' : ''}
                {validation.costVariance.toFixed(2)} {t('common.currency')}
              </span>
            </div>
          </div>

          {/* Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="text-sm">{t('inventory.deleteValidation.warning')}</p>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
